import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../../lib/api';
import { Bell, Check, CheckCheck, X, Wrench, CalendarDays, DollarSign, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useI18n } from '../../i18n';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { getNotificationDestination } from '../../lib/notificationRoutes';

const typeIcons = {
  meeting_created: CalendarDays,
  meeting_updated: CalendarDays,
  meeting_cancelled: CalendarDays,
  meeting_reminder: CalendarDays,
  maintenance_status: Wrench,
  maintenance_overdue: Wrench,
  maintenance_scheduled: Wrench,
  payment_due: DollarSign,
  payment_overdue: DollarSign,
};

const typeColors = {
  meeting_created: 'text-domio-green',
  meeting_cancelled: 'text-red-500',
  meeting_reminder: 'text-domio-600',
  maintenance_status: 'text-amber-500',
  maintenance_overdue: 'text-red-500',
  maintenance_scheduled: 'text-domio-600',
};

const NotificationBell = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dismissingId, setDismissingId] = useState(null);
  const [clearingAll, setClearingAll] = useState(false);
  const ref = useRef(null);

  const fetchNotifications = async () => {
    try {
      const res = await notificationsAPI.getInApp();
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      /* ignore poll errors */
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      fetchNotifications();
    } catch {
      toast.error(t('notificationBell.dismissError'));
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      fetchNotifications();
    } catch {
      toast.error(t('notificationBell.dismissError'));
    }
  };

  const handleDismiss = async (e, id) => {
    e.stopPropagation();
    setDismissingId(id);
    try {
      await notificationsAPI.dismiss(id);
      await fetchNotifications();
    } catch {
      toast.error(t('notificationBell.dismissError'));
      await fetchNotifications();
    } finally {
      setDismissingId(null);
    }
  };

  const handleClearAll = async () => {
    setClearingAll(true);
    try {
      await notificationsAPI.clearAllInApp();
      setNotifications([]);
      setUnreadCount(0);
      toast.success(t('notificationBell.clearedAll'));
    } catch {
      toast.error(t('notificationBell.dismissError'));
      await fetchNotifications();
    } finally {
      setClearingAll(false);
    }
  };

  const openNotification = async (n) => {
    const dest = getNotificationDestination(n.type, n.metadata || {});
    try {
      if (!n.read) await notificationsAPI.markRead(n.id);
    } catch {
      /* non-blocking */
    }
    navigate(`${dest.pathname}${dest.search || ''}`);
    setOpen(false);
    fetchNotifications();
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('notificationBell.justNow');
    if (mins < 60) return t('notificationBell.minutesAgo', { count: String(mins) });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t('notificationBell.hoursAgo', { count: String(hrs) });
    return t('notificationBell.daysAgo', { count: String(Math.floor(hrs / 24)) });
  };

  const hasItems = notifications.length > 0;

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(!open)}
        className="relative text-slate-400 hover:text-white hover:bg-[#0A2F52]"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="absolute left-full bottom-0 ml-2 w-80 bg-white rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
            <h3 className="font-manrope font-bold text-sm text-slate-900 truncate">
              {t('notificationBell.title')}
            </h3>
            <div className="flex items-center gap-1 flex-shrink-0">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  className="text-xs text-domio-600 hover:underline flex items-center gap-1 px-1"
                  data-testid="mark-all-read"
                >
                  <CheckCheck className="h-3 w-3" /> {t('notificationBell.markAllRead')}
                </button>
              )}
              {hasItems && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      type="button"
                      disabled={clearingAll}
                      className="text-xs text-slate-600 hover:text-red-600 hover:underline px-1 disabled:opacity-50"
                      data-testid="clear-all-notifications"
                    >
                      {t('notificationBell.clearAll')}
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('notificationBell.clearAllConfirmTitle')}</AlertDialogTitle>
                      <AlertDialogDescription>{t('notificationBell.clearAllConfirmBody')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-red-600 hover:bg-red-700"
                        onClick={handleClearAll}
                      >
                        {t('notificationBell.clearAll')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {!hasItems ? (
              <div className="p-6 text-center text-slate-400 text-sm">{t('notificationBell.empty')}</div>
            ) : (
              notifications.map((n) => {
                const Icon = typeIcons[n.type] || Bell;
                const color = typeColors[n.type] || 'text-slate-500';
                return (
                  <div
                    key={n.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openNotification(n)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openNotification(n);
                      }
                    }}
                    className={cn(
                      'group flex items-start gap-2 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer',
                      !n.read && 'bg-domio-50/30'
                    )}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={cn('mt-0.5 flex-shrink-0', color)}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm text-slate-700 leading-snug', !n.read && 'font-medium')}>
                        {n.title}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                    <div className="flex flex-shrink-0 items-start gap-0.5">
                      {!n.read && (
                        <button
                          type="button"
                          title={t('notificationBell.markRead')}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkRead(n.id);
                          }}
                          className="text-slate-300 hover:text-domio-600 mt-0.5 p-1 rounded"
                          data-testid={`notification-mark-read-${n.id}`}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        title={t('notificationBell.dismiss')}
                        disabled={dismissingId === n.id}
                        onClick={(e) => handleDismiss(e, n.id)}
                        className="text-slate-300 hover:text-red-600 mt-0.5 p-1 rounded opacity-70 group-hover:opacity-100"
                        data-testid={`notification-dismiss-${n.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
